import { Dispatch } from '@reduxjs/toolkit'
import { QuickTradeData, setQuickOrderData } from '../reducer/orders.reducer'
import { Broker_OrderType, OrderType, Product, TransactionType } from '../proto/generated/order'
import { Security, SecurityCategory_CategoryCode } from '../proto/generated/security'
import { isMtfEligibleCashSecurity } from './cashInstrument'
import { quoteReferenceFromScalars, readQuickTradeQuoteScalars } from './quickTradeQuote'
import { getSecurityKey } from './security'

export function resolveQuickTradeProduct(security: Security, product: Product = Product.CNC): Product {
    if (product === Product.INTRADAY) {
        return Product.INTRADAY
    }
    if (product === Product.MTF && isMtfEligibleCashSecurity(security)) {
        return Product.MTF
    }
    if (
        security.categoryCode === SecurityCategory_CategoryCode.FUTURES ||
        security.categoryCode === SecurityCategory_CategoryCode.OPTIONS
    ) {
        return Product.MARGIN
    }
    return Product.CNC
}

export function resolveQuickTradeQuantity(security: Security): number {
    return security.lotSize || 1
}

export function resolveQuickTradeLimitPrice(security: Security, transactionType: TransactionType): number | undefined {
    const securityKey = getSecurityKey(security.exchange, security.code)
    const quote = readQuickTradeQuoteScalars(securityKey)
    const price = quoteReferenceFromScalars(transactionType, quote)
    return price > 0 ? price : undefined
}

export type OpenQuickTradeParams = {
    dispatch: Dispatch
    security: Security
    transactionType: TransactionType
    brokerOrderType?: Broker_OrderType
    orderType?: OrderType
    product?: Product
    price?: number
    mode?: QuickTradeData['mode']
    type?: QuickTradeData['type']
    isSquareOffOrder?: boolean
}

export function dispatchOpenQuickTrade({
    dispatch,
    security,
    transactionType,
    brokerOrderType = Broker_OrderType.MARKET_ORDER,
    orderType = OrderType.NORMAL,
    product = Product.CNC,
    price,
    mode = 'regular',
    type = 'trade',
    isSquareOffOrder = false,
}: OpenQuickTradeParams) {
    const finalProduct = resolveQuickTradeProduct(security, product)
    const quantity = resolveQuickTradeQuantity(security)
    const isLimitOrder = [Broker_OrderType.LIMIT_ORDER, Broker_OrderType.STOPLOSS_ORDER].includes(brokerOrderType)
    const resolvedPrice =
        price !== undefined
            ? price
            : isLimitOrder
              ? resolveQuickTradeLimitPrice(security, transactionType)
              : undefined

    dispatch(
        setQuickOrderData({
            openDialog: true,
            mode,
            type,
            isSquareOffOrder,
            order: {
                transactionType,
                security,
                editOrder: undefined,
                brokerOrderType,
                orderType,
                product: finalProduct,
                price: resolvedPrice,
                quantity,
                maxQuantity: 0,
            },
        })
    )
}
